import {
  formatDuration,
  getDiagnosticAssessmentLabel,
  getSessionSubstitutionRequestSummary,
  isLessonLinkAvailable,
  isMakeupDiagnosticLesson,
} from '@airreading/common';
import { createColumnHelper } from '@tanstack/react-table';
import { Ellipsis } from 'lucide-react';

import { ArrowTooltip } from '@/components/arrow-tooltip';
import { AirIdDisplay } from '@/components/air-id-display';
import { InAppAssessmentTrigger } from '@/components/assessments/in-app/in-app-assessment-trigger';
import { OnDemandGLALink } from '@/components/session/on-demand-gla-link';
import { RequestSessionSubstitute } from '@/components/session/request-session-substitute';
import { SessionScheduleCell } from '@/components/session/session-schedule-cell';
import { SessionStatusBadge } from '@/components/session/session-status-badge';
import { StartOrJoinSession } from '@/components/session/start-or-join-session';
import { startSessionDisabledTooltip } from '@/lib/session-utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SessionCompletionStatusCell } from '@/routes/_layout/sessions/-components/session-completion-status-cell';
import { StudentNeedsIndicators } from '@/components/student-needs-indicators';
import {
  ApiTargetUser,
  CoachPortalSession,
  CoachPortalSessionsResults,
  VideoConferenceTypeLabels,
  studentYearOption,
} from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { TimeZoneTruncated } from '@/components/time-zone-truncated';
import { tableCellStackNarrowClassName } from '@/components/table/table-cell-stack-narrow';
import { recordSessionLessonLinkOpened } from '@/lib/api-calls';
import { cn } from '@/lib/tailwind-utils';
import { CopyableLink } from '../copyable-link';
import { SessionFeedbackStatus } from './session-feedback-status';

export const CoachSessionsTableColumnIdSchema = z.enum([
  'students',
  'startInstant',
  'status',
  'class_id',
  'class_button',
  'lesson',
  'action',
  'duration',
  'school',
  'feedback',
  'note',
  'class_status',
  'join',
]);
export type CoachSessionsTableColumnId = z.infer<typeof CoachSessionsTableColumnIdSchema>;

const coachCol = createColumnHelper<CoachPortalSessionsResults['coachSessions'][number]>();
export const allCoachColumns = ({
  actingUser,
  onStartOrJoinSessionButtonClick,
  tablePrefix,
}: {
  actingUser: ApiTargetUser;
  onStartOrJoinSessionButtonClick?: (row: CoachPortalSession) => void;
  tablePrefix?: string;
}) => [
  coachCol.accessor((row) => row, {
    id: CoachSessionsTableColumnIdSchema.enum.startInstant,
    header: () => (
      <span className="flex items-center whitespace-nowrap">
        Time (
        <TimeZoneTruncated timeZone={actingUser.timeZone || Temporal.Now.timeZoneId()} />)
      </span>
    ),
    cell: ({ getValue }) => {
      const { startInstant, endInstant } = getValue();
      return (
        <SessionScheduleCell
          {...{ startInstant, endInstant }}
          displayTimeZone={actingUser.timeZone || Temporal.Now.timeZoneId()}
        />
      );
    },
    enableSorting: true,
  }),

  coachCol.accessor((row) => row, {
    id: CoachSessionsTableColumnIdSchema.enum.students,
    header: 'Students',
    cell: ({ getValue }) => {
      const session = getValue();
      const students = session.students;
      const isMakeupDiagnostic =
        isMakeupDiagnosticLesson(session.lesson.name) &&
        students.some((s) => s.needsDiagnosticAssessment !== undefined);
      return (
        <div className={cn('flex flex-wrap gap-1', tableCellStackNarrowClassName)}>
          {[...students]
            .sort((a, b) => (a.coachFacingDisplayName ?? '').localeCompare(b.coachFacingDisplayName ?? ''))
            .map((s) => {
              const onDemandGLA = session.onDemandGLAs.find(({ studentId }) => studentId === s.id);
              return (
                <div key={s.id} className="flex flex-wrap items-center justify-start gap-2">
                  <Badge variant="outline" className="text-left rounded-sm px-1 w-fit">
                    <span className="inline-flex items-center gap-1">
                      <StudentNeedsIndicators specialEd={s.specialEd} dyslexic={s.dyslexic} ell={s.ell} mode="icons" />
                      {s.grade !== null ? (
                        <ArrowTooltip position="bottom" trigger={<span>{s.coachFacingDisplayName ?? '-'}</span>}>
                          {`${studentYearOption(s.grade)} grade`}
                        </ArrowTooltip>
                      ) : (
                        s.coachFacingDisplayName ?? '-'
                      )}
                    </span>
                  </Badge>
                  {isMakeupDiagnostic && getDiagnosticAssessmentLabel(s.needsDiagnosticAssessment)}
                  {onDemandGLA && <OnDemandGLALink onDemandGLA={onDemandGLA} grade={s.grade} />}
                </div>
              );
            })}
        </div>
      );
    },
  }),

  coachCol.accessor((row) => row, {
    id: CoachSessionsTableColumnIdSchema.enum.duration,
    header: 'Duration',
    cell: ({ getValue }) => {
      const { startInstant, endInstant } = getValue();
      const duration = endInstant.since(startInstant, { largestUnit: 'minutes', smallestUnit: 'minutes' });

      return (
        <div className="flex gap-1">
          <span>{formatDuration(duration)}</span>
        </div>
      );
    },
  }),

  coachCol.accessor((row) => row, {
    id: CoachSessionsTableColumnIdSchema.enum.join,
    header: 'Class',
    cell: ({ getValue, row, table }) => {
      const session = getValue();
      const { coachIdWhoIsTeaching } = getSessionSubstitutionRequestSummary(session);
      const isCurrentCoachTeaching = actingUser.id === coachIdWhoIsTeaching;
      const onClick = () => onStartOrJoinSessionButtonClick?.(session);
      const rowIndex = table.getRowModel().rows.findIndex((r) => r.id === row.id);
      const buttonTestId =
        rowIndex >= 0 && tablePrefix ? `${tablePrefix}-table-button-start-${rowIndex + 1}` : undefined;

      return (
        <div className={cn('flex gap-2 items-center', tableCellStackNarrowClassName)}>
          {isCurrentCoachTeaching && (
            <StartOrJoinSession
              label={`Start ${VideoConferenceTypeLabels[session.videoConferenceType] ?? 'Class'}`}
              url={session.meeting?.provider?.hostUrl ?? session.meeting?.provider?.attendeeUrl}
              disabledTooltip={startSessionDisabledTooltip(session.startInstant, session.endInstant)}
              whenUrlEmptyRender="No meeting url"
              startButtonProps={{
                className: 'bg-air-primary hover:bg-air-primary-hover active:bg-air-primary-active',
                icon: undefined,
                size: 'sm',
                onClick,
                'data-testid': buttonTestId,
              }}
            />
          )}
        </div>
      );
    },
  }),

  coachCol.accessor((row) => row, {
    id: CoachSessionsTableColumnIdSchema.enum.lesson,
    header: 'Lesson',
    cell: ({ getValue, row, table }) => {
      const session = getValue();
      const { lesson, lessonLink, startInstant, id } = session;
      const rowIndex = table.getRowModel().rows.findIndex((r) => r.id === row.id);
      const beginAssessmentTestId =
        rowIndex >= 0 && tablePrefix ? `${tablePrefix}-table-button-begin-assessment-${rowIndex + 1}` : undefined;

      return (
        <div className="flex flex-col items-start gap-1">
          {isLessonLinkAvailable(lessonLink, startInstant) ? (
            <CopyableLink
              className="text-air-primary"
              externalHref={lessonLink!}
              canCopyUrl={false}
              showExternalHrefIcon={false}
              onExternalClick={() => {
                void recordSessionLessonLinkOpened(id).catch(() => {});
              }}
            >
              {lesson.name}
            </CopyableLink>
          ) : (
            lesson.name
          )}
          <InAppAssessmentTrigger session={session} dataTestId={beginAssessmentTestId} />
        </div>
      );
    },
  }),

  coachCol.accessor((row) => row, {
    id: CoachSessionsTableColumnIdSchema.enum.status,
    header: 'Status',
    cell: ({ getValue }) => {
      return <SessionStatusBadge status={getValue().status} />;
    },
  }),

  coachCol.accessor((row) => row, {
    id: CoachSessionsTableColumnIdSchema.enum.school,
    header: 'District',
    cell: ({ getValue }) => {
      const { orgName } = getValue();
      return <div>{orgName || '-'}</div>;
    },
  }),

  coachCol.accessor((row) => row, {
    id: CoachSessionsTableColumnIdSchema.enum.class_status,
    header: 'Class status',
    cell: ({ getValue }) => {
      const { sessionCompletionStatus, coachSessionRemarksV2, zoom, startInstant, endInstant } = getValue();
      return (
        <SessionCompletionStatusCell
          status={sessionCompletionStatus}
          coachSessionRemarksV2={coachSessionRemarksV2}
          session={{ zoom, startInstant, endInstant }}
        />
      );
    },
  }),

  coachCol.accessor((row) => row, {
    id: CoachSessionsTableColumnIdSchema.enum.feedback,
    header: 'Feedback',
    cell: ({ getValue }) => {
      const { sessionFeedbacks, hoursCanceledBeforeStart } = getValue();
      return (
        <SessionFeedbackStatus
          feedbacks={sessionFeedbacks}
          hoursCanceledBeforeStart={hoursCanceledBeforeStart}
          lateSentSuffixLabel="(late)"
        />
      );
    },
  }),

  coachCol.accessor((row) => row, {
    id: CoachSessionsTableColumnIdSchema.enum.class_id,
    /** column labeled "Class ID" shows session Air ID */
    header: 'Class ID',
    cell: ({ getValue }) => <AirIdDisplay entityType="ses" rawId={getValue().id} />,
  }),

  coachCol.accessor((row) => row, {
    id: CoachSessionsTableColumnIdSchema.enum.action,
    header: '',
    cell: ({ getValue, row, table }) => {
      const { startInstant, endInstant, id: sessionId, studentGroupId } = getValue();
      const { coachIdWhoCanRequestSub } = getSessionSubstitutionRequestSummary(getValue());
      const canRequestSub = coachIdWhoCanRequestSub === actingUser.id;
      const rowIndex = table.getRowModel().rows.findIndex((r) => r.id === row.id);
      const buttonTestId =
        rowIndex >= 0 && tablePrefix ? `${tablePrefix}-table-button-action-${rowIndex + 1}` : undefined;

      return (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted" data-testid={buttonTestId}>
              <Ellipsis className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <RequestSessionSubstitute
              startInstant={startInstant}
              endInstant={endInstant}
              sessionId={sessionId}
              studentGroupId={studentGroupId}
              disabled={!canRequestSub}
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={!canRequestSub}>
                  Request substitute
                </DropdownMenuItem>
              }
            />
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  }),
];
